import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeForRole } from "./context/AuthContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Schools from "./pages/Schools";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import StudentDetail from "./pages/StudentDetail";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import CashClosing from "./pages/CashClosing";
import Settings from "./pages/Settings";
import UsersPage from "./pages/UsersPage";
import Audit from "./pages/Audit";
import ParentPortal from "./pages/ParentPortal";
import TeacherPortal from "./pages/TeacherPortal";
import Plans from "./pages/Plans";
import PlatformBilling from "./pages/PlatformBilling";
import PlatformPayments from "./pages/PlatformPayments";
import Permisos from "./pages/Permisos";
import PlatformSettings from "./pages/PlatformSettings";
import GuardianDetail from "./pages/GuardianDetail";
import Schedules from "./pages/Schedules";
import Menus from "./pages/Menus";
import Plannings from "./pages/Plannings";
import Nomina from "./pages/Nomina";
import Punch from "./pages/Punch";
import Classroom from "./pages/Classroom";
import Manual from "./pages/Manual";
import Presentacion from "./pages/Presentacion";
import {
  Guardians, Teachers, Subjects, Courses, Activities, Concepts, Discounts,
  Combos, Expenses, Products, BankAccounts, Documents,
} from "./pages/ModulePages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForRole(user)} replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>;
  return <Navigate to={homeForRole(user)} replace />;
}

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("unknown"); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") setStatus("paid");
    } catch (e) { setStatus("unknown"); }
  }, [sessionId]);

  useEffect(() => {
    poll();
    const interval = setInterval(() => { setStatus((prev) => { if (prev !== "paid") poll(); return prev; }); }, 2000);
    return () => clearInterval(interval);
  }, [poll]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-6 p-6">
      <div className="bg-white border rounded-2xl p-10 text-center shadow-sm" data-testid={status === "paid" ? "payment-success-message" : "payment-pending-message"}>
        <h1 className="text-2xl font-bold text-slate-900">{status === "paid" ? "Pago completado" : "Verificando pago…"}</h1>
        <p className="text-slate-500 mt-2">{status === "paid" ? "Tu pago fue procesado correctamente." : "Estamos confirmando tu transacción."}</p>
      </div>
      <Link to="/" className="text-sky-600" data-testid="back-to-home-link">Volver al inicio</Link>
    </div>
  );
}

function PaymentCancel() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-6 p-6">
      <div className="bg-white border border-amber-200 rounded-2xl p-10 text-center shadow-sm" data-testid="payment-cancel-message">
        <h1 className="text-2xl font-bold text-slate-900">Pago cancelado</h1>
        <p className="text-slate-500 mt-2">No se realizó ningún cargo.</p>
      </div>
      <Link to="/" className="text-sky-600" data-testid="back-to-home-link">Volver al inicio</Link>
    </div>
  );
}

const P = (el, roles) => <Protected roles={roles}>{el}</Protected>;
const SCHOOL_ROLES = ["admin", "secretaria", "contabilidad", "maestro"];

function Branding() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/settings`)
      .then((r) => r.json())
      .then((s) => {
        if (s.company_name) document.title = s.company_name;
        if (s.icon_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }
          link.href = s.icon_url;
        }
      })
      .catch(() => {});
  }, []);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
      <Branding />
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registro/:schoolId" element={<Register />} />
          <Route path="/presentacion" element={<Presentacion />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/colegios" element={P(<Schools />, ["superadmin"])} />
          <Route path="/suscripciones" element={P(<Plans />, ["superadmin"])} />
          <Route path="/facturacion" element={P(<PlatformBilling />, ["superadmin"])} />
          <Route path="/pagos-plataforma" element={P(<PlatformPayments />, ["superadmin"])} />
          <Route path="/permisos" element={P(<Permisos />, ["superadmin", "admin"])} />
          <Route path="/empresa" element={P(<PlatformSettings />, ["superadmin"])} />
          <Route path="/padres/:id" element={P(<GuardianDetail />, ["admin", "secretaria"])} />
          <Route path="/dashboard" element={P(<Dashboard />, ["admin", "secretaria", "contabilidad"])} />
          <Route path="/estudiantes" element={P(<Students />, SCHOOL_ROLES)} />
          <Route path="/estudiantes/:id" element={P(<StudentDetail />, [...SCHOOL_ROLES, "padre"])} />
          <Route path="/padres" element={P(<Guardians />, ["admin", "secretaria"])} />
          <Route path="/maestros" element={P(<Teachers />, ["admin", "secretaria"])} />
          <Route path="/materias" element={P(<Subjects />, SCHOOL_ROLES)} />
          <Route path="/cursos" element={P(<Courses />, ["admin"])} />
          <Route path="/actividades" element={P(<Activities />, SCHOOL_ROLES)} />
          <Route path="/horarios" element={P(<Schedules />, ["admin", "secretaria", "maestro"])} />
          <Route path="/menu-comedor" element={P(<Menus />, ["admin", "secretaria", "maestro"])} />
          <Route path="/planificaciones" element={P(<Plannings />, ["admin", "maestro"])} />
          <Route path="/nomina" element={P(<Nomina />, ["admin", "contabilidad"])} />
          <Route path="/classroom" element={P(<Classroom />, ["admin", "maestro"])} />
          <Route path="/ponche/:token" element={<Punch />} />
          <Route path="/tareas" element={P(<TeacherPortal />, ["admin", "secretaria", "maestro"])} />
          <Route path="/calificaciones" element={P(<TeacherPortal />, ["admin", "maestro"])} />
          <Route path="/conceptos" element={P(<Concepts />, ["admin", "contabilidad"])} />
          <Route path="/descuentos" element={P(<Discounts />, ["admin", "contabilidad"])} />
          <Route path="/combos" element={P(<Combos />, ["admin"])} />
          <Route path="/facturas" element={P(<Invoices />, ["admin", "secretaria", "contabilidad"])} />
          <Route path="/pagos" element={P(<Payments />, ["admin", "secretaria", "contabilidad"])} />
          <Route path="/gastos" element={P(<Expenses />, ["admin", "contabilidad"])} />
          <Route path="/productos" element={P(<Products />, ["admin", "contabilidad"])} />
          <Route path="/cierre-caja" element={P(<CashClosing />, ["admin", "contabilidad"])} />
          <Route path="/bancos" element={P(<BankAccounts />, ["admin"])} />
          <Route path="/documentos" element={P(<Documents />, ["admin", "secretaria"])} />
          <Route path="/usuarios" element={P(<UsersPage />, ["admin"])} />
          <Route path="/configuracion" element={P(<Settings />, ["admin"])} />
          <Route path="/auditoria" element={P(<Audit />, ["admin"])} />
          <Route path="/portal" element={P(<ParentPortal />, ["padre"])} />
          <Route path="/maestro" element={P(<TeacherPortal />, ["maestro"])} />
          <Route path="/manual" element={P(<Manual />, ["superadmin", "admin", "secretaria", "contabilidad", "maestro", "padre"])} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
